/**
 * The climate block is the one field stored as raw numbers rather than as a
 * sentence per language: months, units and the humidity band all have to be
 * said differently in Spanish and English, and keeping two parallel copies of
 * those strings in the datasets is how they drift apart. The datasets carry
 * the measurements; this file turns them into words.
 *
 * Every figure is measured at the capital — see `note`. One number for the
 * whole country would be a fiction in Chile, Russia or Brazil, so the card
 * says where it was taken and leaves the country-wide picture to the
 * description that sits above it.
 */

export type HumidityBand = "arid" | "semiarid" | "humid" | "very-humid";

export interface CountryClimate {
  /** The existing free-text description, which covers the whole country. */
  detail: string;
  /** Mean daily minimum of the coldest month, in °C. */
  low: number;
  /** 1-12. */
  lowMonth: number;
  /** Mean daily maximum of the warmest month, in °C. */
  high: number;
  highMonth: number;
  /** Millimetres per year. */
  rain: number;
  rainPeak: number;
  humidity: HumidityBand;
  /** "17:49", or "polar-night" / "midnight-sun" where the sun does neither. */
  sunset: string;
  /** 6 in the southern hemisphere, 12 in the northern one. */
  solsticeMonth: number;
  /** Where the figures were measured. */
  capital: string;
}

const HUMIDITY_KEYS: HumidityBand[] = ["arid", "semiarid", "humid", "very-humid"];

export const isCountryClimate = (value: unknown): value is CountryClimate => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.low === "number" &&
    typeof candidate.high === "number" &&
    typeof candidate.rain === "number" &&
    typeof candidate.sunset === "string" &&
    HUMIDITY_KEYS.includes(candidate.humidity as HumidityBand)
  );
};

const MONTHS = {
  es: [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ],
  en: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],
} as const;

const HUMIDITY_WORDS = {
  es: {
    arid: "árido",
    semiarid: "semiárido",
    humid: "húmedo",
    "very-humid": "muy húmedo",
  },
  en: {
    arid: "arid",
    semiarid: "semi-arid",
    humid: "humid",
    "very-humid": "very humid",
  },
} as const;

// One scale for every country, so two bars can be compared by eye. The ends
// clear the real extremes in the data: Ulaanbaatar's -26 °C in January and
// Kuwait City's 47 °C in July.
export const SCALE_MIN = -30;
export const SCALE_MAX = 50;
export const SCALE_TICKS = [-30, -10, 10, 30, 50];

/** Where a temperature falls on the shared scale, as a 0-100 percentage. */
export const scalePosition = (celsius: number): number => {
  const clamped = Math.min(SCALE_MAX, Math.max(SCALE_MIN, celsius));
  return ((clamped - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100;
};

export interface ClimateCopy {
  low: string;
  lowMonth: string;
  high: string;
  highMonth: string;
  rain: string;
  humidity: string;
  /** Split so the component can emphasise the value without parsing it. */
  sunsetPrefix: string;
  sunsetValue: string;
  sunsetSuffix: string;
  note: string;
  /** Everything above as one sentence, for the listen button. */
  spoken: string;
}

export const describeClimate = (
  climate: CountryClimate,
  isSpanish: boolean
): ClimateCopy => {
  const locale = isSpanish ? "es-AR" : "en-US";
  const months = isSpanish ? MONTHS.es : MONTHS.en;
  const month = (n: number) => months[Math.min(11, Math.max(0, n - 1))];
  const humidity = (isSpanish ? HUMIDITY_WORDS.es : HUMIDITY_WORDS.en)[
    climate.humidity
  ];
  const rain = `${climate.rain.toLocaleString(locale)} mm`;
  const solstice = month(climate.solsticeMonth);

  const low = `${climate.low} °C`;
  const high = `${climate.high} °C`;
  const lowMonth = isSpanish
    ? `mínima media en ${month(climate.lowMonth)}`
    : `average low in ${month(climate.lowMonth)}`;
  const highMonth = isSpanish
    ? `máxima media en ${month(climate.highMonth)}`
    : `average high in ${month(climate.highMonth)}`;

  // Inside the polar circles the shortest day has no sunset to report,
  // because the sun never comes up in the first place. Those two cases get
  // their own sentence rather than being forced through "sunset at …".
  const atSolstice = isSpanish
    ? `En el solsticio de ${solstice} `
    : `At the ${solstice} solstice `;
  let sunsetPrefix: string;
  let sunsetValue: string;
  let sunsetSuffix = "";
  if (climate.sunset === "polar-night") {
    sunsetPrefix = atSolstice;
    sunsetValue = isSpanish ? "no amanece" : "the sun never rises";
  } else if (climate.sunset === "midnight-sun") {
    sunsetPrefix = atSolstice;
    sunsetValue = isSpanish ? "no se pone el sol" : "the sun never sets";
  } else {
    sunsetPrefix = isSpanish ? "Anochece a las " : "Sunset at ";
    sunsetValue = climate.sunset;
    sunsetSuffix = isSpanish
      ? ` en el solsticio de ${solstice}`
      : ` at the ${solstice} solstice`;
  }

  const rainText = isSpanish
    ? `${rain} de lluvia al año, con el pico en ${month(climate.rainPeak)}`
    : `${rain} of rain a year, peaking in ${month(climate.rainPeak)}`;

  const note = isSpanish
    ? `Medido en ${climate.capital}. Un país puede tener varios climas; la descripción de arriba los abarca.`
    : `Measured in ${climate.capital}. A country can hold several climates; the description above covers them.`;

  const sunsetSentence = `${sunsetPrefix}${sunsetValue}${sunsetSuffix}`;
  const spoken = isSpanish
    ? `Clima. ${climate.detail} ${low} de ${lowMonth}, ${high} de ${highMonth}. ${rainText}. Clima ${humidity}. ${sunsetSentence}.`
    : `Climate. ${climate.detail} ${low} ${lowMonth}, ${high} ${highMonth}. ${rainText}. ${humidity} climate. ${sunsetSentence}.`;

  return {
    low,
    lowMonth,
    high,
    highMonth,
    rain: rainText,
    humidity,
    sunsetPrefix,
    sunsetValue,
    sunsetSuffix,
    note,
    spoken,
  };
};
